import React, { forwardRef } from 'react'
import { IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '@aytm/app/swidget/base/questions/Ecommerce/types'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import style from './ProductPreview.module.scss'
import commonStyle from '../Costco.module.scss'
import { formatPriceWithLocale } from '../../functions'
import { stripHighlightTags } from '../../utils/stripHighlightTags'
import Rating from './Rating'
import AddToCartButton from './AddToCartButton'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'

interface IProductPreviewProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  quantity: number
  findability: boolean
  index: number
  disabled?: boolean
  showButtons?: boolean
  fit: number
  applyCustomWidth?: boolean
  showSponsoredLabel?: boolean
  'data-testid'?: string
  onItemSelect: (index: number) => void
  onAddToCart: (qnt: number, index: number) => void
}

const ProductPreview = forwardRef<HTMLDivElement, IProductPreviewProps>(
  (
    {
      schema,
      answer,
      quantity,
      findability,
      index,
      disabled,
      fit,
      showButtons = true,
      showSponsoredLabel = true,
      'data-testid': dataTestId,
      onItemSelect,
      onAddToCart
    },
    ref
  ) => {
    const viewport = useViewport()
    const { t, languageTag } = useSurveyLocale()

    const renderLabels = () => {
      if (1 === answer.prime) {
        return <div className={commonStyle.memberOnly}>{t('questions', 'ec', 'costco', 'memberItem')}</div>
      }
      if (answer.red_label) {
        return <div className={commonStyle.purpleLabel} dangerouslySetInnerHTML={{ __html: answer.red_label }} />
      }
      return answer.green_label ? (
        <div
          className={commonStyle.blueLabel}
          dangerouslySetInnerHTML={{ __html: stripHighlightTags(answer.green_label) }}
        />
      ) : null
    }
    return (
      <div
        ref={ref}
        data-testid={dataTestId}
        className={`
      ${style.productPreview}
      ${disabled ? style.productPreviewDisabled : ''}
      ${viewport.mobile ? style.productPreviewMobile : ''}
      ${fit ? style[`productPreview${fit}PerRow`] : ''}
    `}
      >
        <div className={style.imageWrapper} onClick={() => onItemSelect(index)}>
          {answer.image ? (
            <div
              className={style.image}
              data-testid="SW-EC-Image"
              role="img"
              aria-label={answer.image?.alt_text || t('questions', 'img', 'noAlt')}
              style={{ backgroundImage: `url('${getMediumAssetPath(answer.image)}')` }}
            />
          ) : (
            <div className={style.noImage} />
          )}
        </div>
        <div className={style.contentWrapper}>
          <div className={style.labels}>{renderLabels()}</div>
          <div
            className={style.caption}
            dangerouslySetInnerHTML={{ __html: answer.text }}
            onClick={() => onItemSelect(index)}
          />
          <div className={style.rating}>
            <Rating rating={answer.rating} />
            <div className={style.reviews}>({answer.reviews})</div>
          </div>
          <div className={`${commonStyle.prices} ${commonStyle.pricesMobile}`}>
            {answer.discounted_price && answer.price > answer.discounted_price ? (
              <>
                <div
                  className={commonStyle.newPrice}
                  dangerouslySetInnerHTML={{
                    __html: `${formatPriceWithLocale(answer.discounted_price, schema.price_mask, languageTag, true, false)}`
                  }}
                />
                <div
                  className={commonStyle.lastPrice}
                  dangerouslySetInnerHTML={{
                    __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
                  }}
                />
              </>
            ) : (
              <div
                className={commonStyle.defaultPrice}
                dangerouslySetInnerHTML={{
                  __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
                }}
              />
            )}
          </div>
          {showButtons && (
            <div className={style.delivery}>
              <div className={style.deliveryIcon} />
              <span className={style.deliveryBlackText}>{t('questions', 'ec', 'costco', 'delivery')}&nbsp;</span>
              <span className={style.deliveryGreenText}>{t('questions', 'ec', 'costco', 'available')}</span>
            </div>
          )}
        </div>
        {showButtons && (
          <div className={style.buttonContainer}>
            <AddToCartButton quantity={quantity} findability={findability} index={index} onAddToCart={onAddToCart} />
          </div>
        )}
        {answer.amazon_choice && showSponsoredLabel && (
          <div className={style.sponsored}>{t('questions', 'ec', 'costco', 'sponsored')}</div>
        )}
      </div>
    )
  }
)

ProductPreview.displayName = 'ProductPreview'

export default ProductPreview
